import { ThemeProvider } from "@/components/theme-provider"
import { AuthProvider } from "@/components/auth/auth-provider"
import { CartProvider } from "@/lib/cart"
import { ProtectedRoute } from "@/components/auth/protected-route"
import { AdminRoute } from "@/components/auth/admin-route"
// VOY-506: the global app shell is now the shared v6 TopNav. The old
// `<Header />` (green/teal nav + dark photo background) is retired —
// every route below renders the v6 chrome.
import { TopNav } from "@/components/landing/v6/TopNav"
import { useFirstName } from "@/hooks/useFirstName"
import { useTheme } from "next-themes"
import { CartOverlay } from "@/components/ui/cart-overlay"
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom"
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics"
import { Toaster } from "sonner"

 
import YouPage from "@/pages/you"
import SearchPage from "@/pages/search"
import VoyMyTripPage from "@/pages/voy-my-trip"
import DuffelTestPage from "@/pages/duffel-test"
import SubscriptionPage from "@/pages/subscription"
import SubscriptionSuccessPage from "@/pages/subscription/success"
import GamesPage from "@/pages/games"
import GameSessionPage from "@/pages/game-session"
import QuizPage from "@/pages/quiz"
import LeaderboardPage from "@/pages/leaderboard"
import ProfilePage from "@/pages/profile"
// VOY-485: register the previously orphaned receipts viewer so the new
// Profile's "View all" deep-link works. The component itself
// (src/pages/profile/purchases.tsx) is unchanged — it's functional, just
// never had a route bound to it.
import ProfilePurchasesPage from "@/pages/profile/purchases"
import TripDetailPage from "@/pages/trips/detail"
import SignInPage from "@/pages/auth/sign-in"
import ResetPasswordPage from "@/pages/auth/reset-password"
import AuthCallbackPage from "@/pages/auth/callback"
import PrivacyPage from "@/pages/privacy"
import TermsPage from "@/pages/terms"
import CheckoutSuccessPage from "@/pages/checkout/success"
import CheckoutPage from "@/pages/checkout"
import CheckoutCancelPage from "@/pages/checkout/cancel"
import TestMapPage from "@/pages/test-map"
import TestMapSimplePage from "@/pages/test-map-simple"
import TestMapButtonPage from "@/pages/test-map-button"
import TestRegionAirportsPage from "@/pages/test-region-airports"
import TestBookingStatusPage from "@/pages/test-booking-status"
import CompanionPage from "@/pages/companion"
import GameMockupPage from "@/pages/game-mockup"
import { LandingV6 } from "@/components/landing/v6"

/**
 * VOY-506: the global v6 app shell.
 *
 * Wraps every route in `<div class="voy-v6 [dark] min-h-screen">` so
 * the v6 design tokens cascade, the shared `<TopNav />` styles apply,
 * and the v6 surface color fills the viewport (the body's default
 * bg won't bleed through under short-content pages like auth/legal/
 * checkout-cancel). The `dark` class is bound to next-themes'
 * resolvedTheme so the v6 dark palette tracks the user's theme.
 *
 * The shell renders the TopNav unconditionally — landing, admin,
 * checkout, every route. Pages that need their own .voy-v6 .page
 * scroll container (Profile, You) keep that — wrapping inside an
 * already-wrapped .voy-v6 is a no-op for CSS variables.
 */
function AppShell({ children }: { children: React.ReactNode }) {
  const { resolvedTheme } = useTheme()
  // Avatar initial: resolves from `profiles.name` → OAuth full_name →
  // undefined, via useFirstName (same hook the old LandingV6 used).
  // No email-local fallback by design — strings like "jmdarre.reg.1"
  // read as usernames, not names. Undefined keeps the TopNav's
  // "Sign in" ghost link visible instead of a placeholder avatar.
  // Strip the first alpha character so digits at the start of a name
  // (rare but possible) don't surface as the avatar glyph.
  const firstName = useFirstName()
  const initial = firstName
    ? (firstName.match(/[a-zA-Z]/)?.[0] ?? "").toUpperCase() || undefined
    : undefined
  const wrapperClass = resolvedTheme === "dark"
    ? "voy-v6 dark min-h-screen"
    : "voy-v6 min-h-screen"
  return (
    <div className={wrapperClass}>
      <TopNav userInitial={initial} />
      <main>{children}</main>
    </div>
  )
}

/**
 * Catches legacy inbound traffic to `/search-plus`. sessionStorage
 * `voySearchData` survives because it's per-tab, not per-route, so
 * the RecentSearchesRow handoff still works. `replace` keeps the
 * redirect out of history so back-button doesn't bounce back here.
 */
function SearchPlusRedirect() {
  const location = useLocation()
  return <Navigate to={`/${location.search}`} replace />
}

// Admin pages
import AdminSitePage from "@/pages/admin/site"
import AdminGamesPage from "@/pages/admin/games"
import AdminQuizPage from "@/pages/admin/quiz"
import AdminApiConfigPage from "@/pages/admin/api-config"
import AdminPreferenceConfigPage from "@/pages/admin/preference-config"

export default function App() {
  useGoogleAnalytics();

  return (
    <ThemeProvider>
      <Router>
        <AuthProvider>
          <CartProvider>
            {/* ✅ Sonner toaster for global toast notifications */}
            <Toaster richColors position="bottom-left" />

            <AppShell>
              <CartOverlay />
              <Routes>
              <Route path="/" element={<LandingV6 />} />
              <Route path="/you" element={<YouPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/voy-my-trip" element={<VoyMyTripPage />} />
              <Route path="/search-plus" element={<SearchPlusRedirect />} />
              <Route path="/duffel-test" element={<DuffelTestPage />} />
              <Route path="/subscription" element={<SubscriptionPage />} />
              <Route path="/subscription/success" element={
                <ProtectedRoute>
                  <SubscriptionSuccessPage />
                </ProtectedRoute>
              } />
              <Route path="/games" element={<GamesPage />} />
              <Route path="/games/:gameId" element={
                <ProtectedRoute>
                  <GameSessionPage />
                </ProtectedRoute>
              } />
              <Route path="/quiz" element={<QuizPage />} />
              <Route path="/leaderboard" element={
                <ProtectedRoute>
                  <LeaderboardPage />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } />
              <Route path="/profile/purchases" element={
                <ProtectedRoute>
                  <ProfilePurchasesPage />
                </ProtectedRoute>
              } />
              <Route path="/trips" element={
                <ProtectedRoute>
                  <Navigate to="/profile?tab=trips" replace />
                </ProtectedRoute>
              } />
              <Route path="/trips/:orderId" element={
                <ProtectedRoute>
                  <TripDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/auth/sign-in" element={<SignInPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/checkout" element={
                <ProtectedRoute>
                  <CheckoutPage />
                </ProtectedRoute>
              } />
              <Route path="/checkout/cancel" element={
                <ProtectedRoute>
                  <CheckoutCancelPage />
                </ProtectedRoute>
              } />
              <Route path="/checkout/success" element={
                <ProtectedRoute>
                  <CheckoutSuccessPage />
                </ProtectedRoute>
              } />
              <Route path="/test-map" element={<TestMapPage />} />
              <Route path="/test-map-simple" element={<TestMapSimplePage />} />
              <Route path="/test-map-button" element={<TestMapButtonPage />} />
              <Route path="/test-region-airports" element={<TestRegionAirportsPage />} />
              <Route path="/test-booking-status" element={<TestBookingStatusPage />} />
              <Route path="/companion" element={<CompanionPage />} />
              <Route path="/game-mockup" element={<GameMockupPage />} />
              
              {/* Admin Routes */}
              <Route path="/admin/site" element={
                <AdminRoute>
                  <AdminSitePage />
                </AdminRoute>
              } />
              <Route path="/admin/games" element={
                <AdminRoute>
                  <AdminGamesPage />
                </AdminRoute>
              } />
              <Route path="/admin/quiz" element={
                <AdminRoute>
                  <AdminQuizPage />
                </AdminRoute>
              } />
              <Route path="/admin/api-config" element={
                <AdminRoute>
                  <AdminApiConfigPage />
                </AdminRoute>
              } />
              <Route path="/admin/preferences" element={
                <AdminRoute>
                  <AdminPreferenceConfigPage />
                </AdminRoute>
              } />
            </Routes>
            </AppShell>
          </CartProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  )
}
